/**
 * "The Cat" — a walkable dark room. The player roams a floor (X lateral,
 * Z forward). In the centre sits Schrödinger's box with a tempting red OPEN
 * button: pressing it *observes* the cat (meow → collapse → back to Level 1).
 * The way on is to NOT look — walk around to the EXIT door at the far end.
 * Pure + deterministic: no Babylon/DOM.
 */
export interface CatRoomState {
  t: number;
  pos: { x: number; z: number };
  facing: number; // heading angle for the avatar
  strideAccum: number;
  nearButton: boolean;
  opened: boolean;
  exited: boolean;
}

export interface CatRoomInput {
  /** Stick vector: x strafes, y is down-positive (so up = forward). */
  move: { x: number; y: number };
  interact: boolean; // edge
}

export interface CatRoomConfig {
  speed: number;
  xMin: number;
  xMax: number;
  zMin: number;
  zMax: number;
  /** Box AABB (the player walks around it). */
  box: { x: number; z: number; hx: number; hz: number };
  button: { x: number; z: number; radius: number };
  exit: { x: number; z: number; halfW: number };
  playerRadius: number;
  strideLength: number;
}

export type CatRoomEvent =
  | { type: 'footstep' }
  | { type: 'opened' } // pressed OPEN → observed → reset
  | { type: 'exit' };

export const DEFAULT_CATROOM_CONFIG: CatRoomConfig = {
  speed: 4.5,
  xMin: -8,
  xMax: 8,
  zMin: 0,
  zMax: 30,
  box: { x: 0, z: 14, hx: 1.8, hz: 1.3 },
  button: { x: 0, z: 12.4, radius: 2.4 },
  exit: { x: 0, z: 27, halfW: 2.2 },
  playerRadius: 0.5,
  strideLength: 1.4,
};

export function createCatRoomState(): CatRoomState {
  return {
    t: 0,
    pos: { x: 0, z: 3.5 },
    facing: 0,
    strideAccum: 0,
    nearButton: false,
    opened: false,
    exited: false,
  };
}

export function stepCatRoom(
  s: CatRoomState,
  input: CatRoomInput,
  dt: number,
  cfg: CatRoomConfig,
): CatRoomEvent[] {
  const events: CatRoomEvent[] = [];
  s.t += dt;
  if (s.opened || s.exited) return events;

  // Move (up on the stick = forward).
  const mx = input.move.x;
  const mz = -input.move.y;
  const len = Math.hypot(mx, mz);
  const px = s.pos.x;
  const pz = s.pos.z;
  if (len > 0.01) {
    s.facing = Math.atan2(mx, mz);
    s.pos.x += (mx / Math.max(1, len)) * cfg.speed * dt * Math.min(1, len);
    s.pos.z += (mz / Math.max(1, len)) * cfg.speed * dt * Math.min(1, len);
  }

  // Walls.
  const r = cfg.playerRadius;
  s.pos.x = Math.max(cfg.xMin + r, Math.min(cfg.xMax - r, s.pos.x));
  s.pos.z = Math.max(cfg.zMin + r, Math.min(cfg.zMax - r, s.pos.z));

  // Box collision (push out along the smaller overlap axis).
  const b = cfg.box;
  const dx = s.pos.x - b.x;
  const dz = s.pos.z - b.z;
  const ox = b.hx + r - Math.abs(dx);
  const oz = b.hz + r - Math.abs(dz);
  if (ox > 0 && oz > 0) {
    if (ox < oz) s.pos.x = b.x + Math.sign(dx || 1) * (b.hx + r);
    else s.pos.z = b.z + Math.sign(dz || 1) * (b.hz + r);
  }

  // Footsteps by distance actually walked.
  const moved = Math.hypot(s.pos.x - px, s.pos.z - pz);
  s.strideAccum += moved;
  while (s.strideAccum >= cfg.strideLength) {
    s.strideAccum -= cfg.strideLength;
    events.push({ type: 'footstep' });
  }

  // Button proximity + press.
  s.nearButton = Math.hypot(s.pos.x - cfg.button.x, s.pos.z - cfg.button.z) <= cfg.button.radius;
  if (input.interact && s.nearButton) {
    s.opened = true;
    events.push({ type: 'opened' });
    return events;
  }

  // Exit.
  if (s.pos.z >= cfg.exit.z && Math.abs(s.pos.x - cfg.exit.x) <= cfg.exit.halfW) {
    s.exited = true;
    events.push({ type: 'exit' });
  }

  return events;
}
